import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { List, Map } from 'immutable';
import { Loader } from '@Sage/hoi-poi-ui';
import { COPILOT_CHAT_FEATURES } from '@Sage/ssm-web5';

import SizeComponent from 'components/SizeComponent';
import { COMPANIES, OPPORTUNITIES, SALESORDERS } from 'constants/Entities';
import {
    WidgetContentLayout,
    WidgetEmpty,
    WidgetHeaderLayout,
    WidgetLayout,
} from 'containers/components/widgets/Layouts';
import { ENTITY_MODAL_UPDATE } from 'lib/events';
import { subscribe } from 'lib/EventBuser';
import Context from 'managers/Context';
import useDisableCreateSalesOrders from 'hooks/useDisableCreateSalesOrders';
import { EntityModalActions } from 'actions';
import { getLiteral } from 'utils/getLiteral';
import { getSalesOrdersUrl } from 'utils/getUrl';
import { logEvent } from 'utils/tracking';

import SalesOrdersWidgetTable from './components/SalesOrdersWidgetTable';

import './styles.scss';

const PAGE_SIZE = 200;

const SalesOrdersWidget = memo(({ data, entityId, entityName, entityType, id, setSize }) => {
    const dispatch = useDispatch();
    const createByPermission = useSelector(
        (state) => state.config.permission.crud_permission[SALESORDERS.permission]?.create ?? false,
    );
    const disableCreateSalesOrders = useDisableCreateSalesOrders(data);

    const create = useMemo(
        () => (disableCreateSalesOrders === true ? false : createByPermission),
        [disableCreateSalesOrders, createByPermission],
    );

    const [state, setState] = useState({
        counterClosed: 0,
        counterOpen: 0,
        firstLoading: true,
        listClosed: [],
        listOpen: [],
        loadingClosed: true,
        loadingOpen: true,
        numPageClosed: 0,
        numPageOpen: 0,
        tabSelected: 0,
    });

    const firstLoadingRef = useRef(false);
    const modalUnsubscriptionRef = useRef(null);

    const getSalesOrders = useCallback((filter, numPage, list, success, error) => {
        const offset = numPage * PAGE_SIZE;
        const newFilters = filter.set('skipExtrafields', {
            custom: true,
            value: true,
        });

        Context.entityManager.getEntitiesManager(SALESORDERS).getOrders(
            offset,
            PAGE_SIZE,
            newFilters,
            (ordersList, totalRows) => {
                const newList = list.concat(ordersList);
                success({
                    counter: totalRows,
                    list: newList,
                });
            },
            error,
        );
    }, []);

    const getOpenSalesOrders = useCallback(
        (success, error) => {
            let filterOpen = new Map({
                endState: { custom: true, filterDataType: 'int', value: 2 },
            });
            filterOpen = filterOpen.set(entityType.entity, {
                custom: true,
                filterDataType: 'singleValueList',
                value: List([{ Id: entityId }]),
            });

            getSalesOrders(filterOpen, state.numPageOpen, state.listOpen, success, error);
        },
        [entityId, entityType.entity, getSalesOrders, state.listOpen, state.numPageOpen],
    );

    const getCloseSalesOrders = useCallback(
        (success, error) => {
            let filterClosed = new Map({
                endState: { custom: true, filterDataType: 'int', value: 1 },
            });
            filterClosed = filterClosed.set(entityType.entity, {
                custom: true,
                filterDataType: 'singleValueList',
                value: List([{ Id: entityId }]),
            });

            getSalesOrders(filterClosed, state.numPageClosed, state.listClosed, success, error);
        },
        [entityId, entityType.entity, getSalesOrders, state.listClosed, state.numPageClosed],
    );

    const getAllSalesOrders = useCallback(() => {
        const openPromise = new Promise((resolve, reject) => {
            getOpenSalesOrders(resolve, reject);
        });

        const closePromise = new Promise((resolve, reject) => {
            getCloseSalesOrders(resolve, reject);
        });

        Promise.all([openPromise, closePromise]).then((result) => {
            setState((prev) => ({
                ...prev,
                counterClosed: result[1].counter,
                counterOpen: result[0].counter,
                firstLoading: false,
                listClosed: result[1].list,
                listOpen: result[0].list,
                loadingClosed: false,
                loadingOpen: false,
                numPageClosed: prev.numPageClosed + 1,
                numPageOpen: prev.numPageOpen + 1,
            }));
        });
    }, [getCloseSalesOrders, getOpenSalesOrders]);

    const getMoreOpenSalesOrders = useCallback(() => {
        setState((prev) => ({ ...prev, loadingOpen: true }));
        getOpenSalesOrders(
            (result) => {
                setState((prev) => ({
                    ...prev,
                    listOpen: result.list,
                    loadingOpen: false,
                    numPageOpen: prev.numPageOpen + 1,
                }));
            },
            () => {},
        );
    }, [getOpenSalesOrders]);

    const getMoreCloseSalesOrders = useCallback(() => {
        setState((prev) => ({ ...prev, loadingClosed: true }));
        getCloseSalesOrders(
            (result) => {
                setState((prev) => ({
                    ...prev,
                    listClosed: result.list,
                    loadingClosed: false,
                    numPageClosed: prev.numPageClosed + 1,
                }));
            },
            () => {},
        );
    }, [getCloseSalesOrders]);

    const refreshSalesOrders = useCallback((orderId, isPrincipal) => {
        setState((prev) => {
            const updatedOpen = prev.listOpen.map((current) => ({
                ...current,
                principal: current.id === orderId ? isPrincipal : false,
            }));

            const updatedClosed = prev.listClosed.map((current) => ({
                ...current,
                principal: current.id === orderId ? isPrincipal : false,
            }));

            return {
                ...prev,
                listClosed: updatedClosed,
                listOpen: updatedOpen,
            };
        });
    }, []);

    const handleSetSize = useCallback(
        (size) => {
            setSize?.({
                ...size,
                height: 260,
            });
        },
        [setSize],
    );

    const onSelectTab = useCallback((event, tabSelected) => {
        setState((prev) => ({ ...prev, tabSelected }));
    }, []);

    const onClickAdd = useCallback(() => {
        logEvent({
            event: entityType.trueName,
            functionality: 'create',
            submodule: 'salesOrdersWidget',
        });

        const modalData = {};

        if (data.tarifa) {
            modalData.idRate = {
                label: data.tarifa,
                value: data.idTarifa,
            };
        }

        if (entityType?.entity === COMPANIES.entity && data?.id) {
            modalData.idCompany = {
                label: data?.name ?? '',
                value: data?.id,
            };
        }

        if (entityType?.entity === OPPORTUNITIES.entity && data?.id) {
            modalData.idOpportunity = {
                label: data?.name ?? '',
                value: data?.id,
            };

            if (data?.companyId) {
                modalData.idCompany = {
                    label: data.companyDescription,
                    value: data.companyId,
                };
            }
        }

        if (data?.idEnvironment) {
            modalData.idBranch = data.idEnvironment;
        }

        dispatch(
            EntityModalActions.init({
                data: modalData,
                entity: SALESORDERS,
                hideDelete: true,
                labels: {
                    error: getLiteral('label_failed_create'),
                    success: getLiteral('succes_entitycreatedsuccessfully'),
                    title: getLiteral('title_add_salesorder'),
                },
                size: 'big',
            }),
        );
    }, [data, dispatch, entityType]);

    const onClickSeeAll = useCallback(() => {
        const targetId = id ?? entityId;
        const anchorSeeAll = getSalesOrdersUrl(
            SALESORDERS.entity,
            'filter',
            entityType.entity,
            targetId,
            entityName,
        );
        window.location.href = anchorSeeAll;
    }, [entityId, entityName, entityType.entity, id]);

    useEffect(() => {
        if (entityId) {
            firstLoadingRef.current = false;
            getAllSalesOrders();
        }

        modalUnsubscriptionRef.current = subscribe(
            `${ENTITY_MODAL_UPDATE}--${SALESORDERS.entity}`,
            () => {
                firstLoadingRef.current = true;
                setState({
                    counterClosed: 0,
                    counterOpen: 0,
                    firstLoading: true,
                    listClosed: [],
                    listOpen: [],
                    loadingClosed: true,
                    loadingOpen: true,
                    numPageClosed: 0,
                    numPageOpen: 0,
                    tabSelected: 0,
                });
            },
        );

        return () => {
            modalUnsubscriptionRef.current?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!entityId) {
            setState((prev) => ({ ...prev, firstLoading: true }));
            firstLoadingRef.current = true;
        } else if (entityId && firstLoadingRef.current) {
            firstLoadingRef.current = false;
            getAllSalesOrders();
        }
    }, [entityId, getAllSalesOrders]);

    const numOpen = useMemo(() => {
        return state.counterOpen === -1 || state.counterOpen === 'undefined'
            ? 0
            : state.counterOpen;
    }, [state.counterOpen]);

    const numClosed = useMemo(() => {
        return state.counterClosed === -1 || state.counterClosed === 'undefined'
            ? 0
            : state.counterClosed;
    }, [state.counterClosed]);

    const widgetTabs = useMemo(
        () => [
            {
                count: numOpen || '0',
                index: 0,
                title: getLiteral('label_open_salesorders'),
            },
            {
                count: numClosed || '0',
                index: 1,
                title: getLiteral('label_closed_salesorders'),
            },
        ],
        [numClosed, numOpen],
    );

    const copilotLauncherConfig = useMemo(
        () => ({
            entity: entityType,
            entityId,
            feature: COPILOT_CHAT_FEATURES.SALES_ORDERS_ANALYZE,
            name: data.name,
        }),
        [data.name, entityId, entityType],
    );

    const showLoader = useMemo(
        () => state.firstLoading || (state.loadingOpen && state.loadingClosed),
        [state.firstLoading, state.loadingClosed, state.loadingOpen],
    );

    return (
        <SizeComponent>
            <WidgetLayout
                data={entityId && entityType}
                loading={state.firstLoading}
                className="fm-sales-orders-widget"
            >
                <WidgetHeaderLayout
                    className="fm-sales-orders-widget-header"
                    content={widgetTabs}
                    tabControlled={state.tabSelected}
                    onSelectTab={onSelectTab}
                    onClickAdd={onClickAdd}
                    onClickSeeAll={onClickSeeAll}
                    permissions={{ create }}
                    copilotLauncherConfig={copilotLauncherConfig}
                />
                <WidgetContentLayout className="fm-sales-orders-widget-content">
                    <SizeComponent setSize={!numOpen && !numClosed ? setSize : handleSetSize}>
                        {showLoader && <WidgetEmpty icon={<Loader />} />}
                        {!state.firstLoading && (
                            <SalesOrdersWidgetTable
                                counter={state.counterOpen}
                                readonly={disableCreateSalesOrders}
                                entityType={entityType}
                                getSalesOrders={getMoreOpenSalesOrders}
                                isVisible={state.tabSelected === 0}
                                list={state.listOpen}
                                loading={state.loadingOpen}
                                refreshSalesOrders={refreshSalesOrders}
                                tabSelected={state.tabSelected}
                            />
                        )}
                        {!state.firstLoading && (
                            <SalesOrdersWidgetTable
                                counter={state.counterClosed}
                                readonly={disableCreateSalesOrders}
                                entityType={entityType}
                                getSalesOrders={getMoreCloseSalesOrders}
                                isVisible={state.tabSelected === 1}
                                list={state.listClosed}
                                loading={state.loadingClosed}
                                refreshSalesOrders={refreshSalesOrders}
                                tabSelected={state.tabSelected}
                            />
                        )}
                    </SizeComponent>
                </WidgetContentLayout>
            </WidgetLayout>
        </SizeComponent>
    );
});

SalesOrdersWidget.displayName = 'SalesOrdersWidget';

export default SalesOrdersWidget;
